import React, { Fragment } from 'react';

// === Components === //
import Title from '@components/sections/Title';
import Paragraph from '@components/common/Paragraph';
import ListWithIcon from '@components/sections/ListWithIcon';
import WarningBox from '@components/sections/WarningBox';
import BackgroundBox from '@components/sections/BackgroundBox';
import ButtonLinkWrapper from '@components/sections/ButtonLinkWrapper';
import Symptoms from '@components/sections/Symptoms';
import Container from '@components/common/Container';
import ImageWithText from '@components/sections/ImageWithText';
import PublicationsItem from '@components/sections/PublicationsItem';
import MultiLine from '@components/sections/MultiLine';
import Table from '@components/sections/Table';
import Events from '@components/sections/Events';
import SocialMedia from '@components/sections/SocialMedia';
import Download from '@components/sections/Download';
import ImageFull from '@components/common/ImageFull';
import SpeechBalloons from '@components/sections/SpeechBalloons';

// === Content === //
import warning_icon from '@assets/svg/warning_icon.svg';

// === Helpers === //
import get from 'lodash.get';
import * as utils from '@utils';

// === Types === //
import { PageSectionData, EventData } from '@content/types/page';
import { Variant } from '@content/types/layout';

interface PageSectionsProps {
  sections: PageSectionData[];
  variant: Variant;
  events: EventData[];
}

const PageSections = ({ sections, variant, events }: PageSectionsProps) => {
  return (
    <div>
      {sections &&
        sections.length > 0 &&
        sections.map((section, index) => (
          <div
            key={section.uuid}
            id={
              section && section.title ? utils.slugify(get(section, 'id', '')) : `section-${index}`
            }
          >
            {get(section, 'components', []).map((component, index) => (
              <Fragment key={get(component, 'uuid', `component-${index}`)}>
                {component && component.entityBundle === 'title' && component.fieldTitle && (
                  <Container>
                    <Title
                      text={get(component, 'fieldTitle', '')}
                      variant={variant}
                      alignRight={get(component, 'fieldRightText', []).length === 0 ? false : true}
                    />
                  </Container>
                )}
                {component && component.entityBundle === 'content' && component.fieldLongText && (
                  <Container>
                    <Paragraph text={get(component, 'fieldLongText.value', '')} variant={variant} />
                  </Container>
                )}
                {component &&
                  component.entityBundle === 'image' &&
                  component?.fieldImages.length > 0 &&
                  component?.fieldImages[0]?.entity?.entityBundle === 'image_with_caption' && (
                    <ListWithIcon
                      data={get(component, 'fieldImages', []).map((img) => ({
                        icon: get(img, 'entity.fieldIcon.entity.url', ''),
                        alt: get(img, 'entity.fieldIcon.alt', ''),
                        title: get(img, 'entity.fieldIcon.title', ''),
                        text: get(img, 'entity.fieldCaption.value', ''),
                      }))}
                      background={get(component, 'fieldBackground', []).length === 0 ? false : true}
                      variant={variant}
                    />
                  )}
                {component &&
                  component.entityBundle === 'image' &&
                  component?.fieldImages.length > 0 &&
                  component?.fieldImages[0]?.entity?.entityBundle === 'image_center' && (
                    <Container>
                      {get(component, 'fieldImages', []).map((img, index) => (
                        <ImageFull
                          key={get(img, 'uuid', index)}
                          alt={
                            get(img, 'entity.fieldSvgImage.alt', undefined) ||
                            get(img, 'entity.fieldImage.alt', '')
                          }
                          title={
                            get(img, 'entity.fieldSvgImage.title', undefined) ||
                            get(img, 'entity.fieldImage.title', '')
                          }
                          imageDesktop={
                            get(img, 'entity.fieldSvgImage.entity.url', undefined) ||
                            get(img, 'entity.fieldImage.url', '')
                          }
                          imageMobile={
                            get(img, 'entity.fieldSvgImageMobile.entity.url', undefined) ||
                            get(img, 'entity.fieldImageMobile.url', '')
                          }
                        />
                      ))}
                    </Container>
                  )}
                {component &&
                  component.entityBundle === 'warning_text' &&
                  component.fieldWarning && (
                    <WarningBox
                      src={warning_icon}
                      text={get(component, 'fieldWarning.value', '')}
                    />
                  )}
                {component &&
                  component.entityBundle === 'background_section' &&
                  component.fieldLayoutBg && (
                    <BackgroundBox
                      variant={variant}
                      text={get(component, 'fieldLayoutBg', [])}
                      centered={get(component, 'fieldCenter', []).length === 0 ? false : true}
                    />
                  )}
                {component && component.entityBundle === 'button_link' && component.fieldLink && (
                  <ButtonLinkWrapper
                    link={get(component, 'fieldLink.url.path', '')}
                    variant={variant}
                  >
                    {utils.SafeHtml(get(component, 'fieldLink.title', ''))}
                  </ButtonLinkWrapper>
                )}
                {component &&
                  component.entityBundle === 'symptoms_patients' &&
                  component.fieldInfo && <Symptoms data={component} variant={variant} />}
                {component && component.entityBundle === 'text_left_image_right' && (
                  <ImageWithText
                    src={
                      get(component, 'fieldSvgImage.entity.url', undefined) ||
                      get(component, 'fieldImage.url', '')
                    }
                    alt={
                      get(component, 'fieldSvgImage.alt', undefined) ||
                      get(component, 'fieldImage.alt')
                    }
                    text={get(component, 'fieldLongText.processed', '')}
                    title={get(component, 'fieldTitle', '')}
                    variant={variant}
                  />
                )}
                {component && component.entityBundle === 'image_left_text_right' && (
                  <ImageWithText
                    src={
                      get(component, 'fieldSvgImage.entity.url', undefined) ||
                      get(component, 'fieldImage.url', '')
                    }
                    alt={
                      get(component, 'fieldSvgImage.alt', undefined) ||
                      get(component, 'fieldImage.alt')
                    }
                    text={get(component, 'fieldLongText.processed', '')}
                    title={get(component, 'fieldTitle', '')}
                    variant={variant}
                    reverse={true}
                  />
                )}
                {component && component.entityBundle === 'publication' && (
                  <PublicationsItem
                    id={get(component, 'fieldId', '')}
                    url={get(component, 'fieldLink.url.path', '')}
                    urlLabel={get(component, 'fieldLink.title', '')}
                    title={get(component, 'fieldTitle', '')}
                    subTitle={get(component, 'fieldSubtitle', '')}
                    variant={variant}
                  />
                )}
                {component && component.entityBundle === 'multi_lines' && (
                  <MultiLine
                    data={get(component, 'fieldColumns', []).map((column) => ({
                      alt: get(column, 'entity.fieldIcon.alt', ''),
                      url: get(column, 'entity.fieldIcon.entity.url', ''),
                      text: get(column, 'entity.fieldLongText.value', ''),
                      id: get(column, 'entity.uuid', ''),
                    }))}
                  />
                )}
                {component && component.entityBundle === 'table' && (
                  <Table
                    header={[
                      get(component, 'fieldColumn1Title', ''),
                      get(component, 'fieldColumn2Title', ''),
                      get(component, 'fieldColumn3Title', ''),
                    ]}
                    body={get(component, 'fieldTableColumns', []).map((row) => {
                      return [
                        get(row, 'entity.fieldColumn1.processed', ''),
                        get(row, 'entity.fieldColumn2', []).map((col, colIndex) => [
                          get(col, 'entity.fieldLongText.processed', ''),
                          get(
                            row,
                            `entity.fieldColumn3[${colIndex}].entity.fieldLongText.processed`,
                            ''
                          ),
                        ]),
                      ];
                    })}
                    variant={variant}
                  />
                )}
                {component && component.entityBundle === 'event' && (
                  <Events list={events} variant={variant} type={get(component, 'fieldType', '0')} />
                )}
                {component && component.entityBundle === 'social_media' && (
                  <SocialMedia
                    facebookLink={get(component, 'fieldFacebook', '')}
                    instagramLink={get(component, 'fieldInstagram', '')}
                    linkedinLink={get(component, 'fieldLinkedin', '')}
                    xingLink={get(component, 'fieldXing', '')}
                    youtubeLink={get(component, 'fieldYoutube', '')}
                    twitterLink={get(component, 'fieldTwitter', '')}
                    button={{
                      url: get(component, 'fieldLink.url.path', ''),
                      label: get(component, 'fieldLink.title', ''),
                    }}
                    variant={variant}
                  />
                )}
                {component && component.entityBundle === 'download' && (
                  <Download
                    variant={variant}
                    preview={{
                      url: get(component, 'fieldImage.url', ''),
                      alt: get(component, 'fieldImage.alt', ''),
                    }}
                    file={get(component, 'fieldDownloadFile.entity.url', '')}
                    ctaLabel={get(component, 'fieldTitle', '')}
                  />
                )}
                {component && component.entityBundle === 'image_and_4_descriptions' && (
                  <SpeechBalloons
                    variant={variant}
                    image={get(component, 'fieldIcon.entity.url', null)}
                    imageAlt={get(component, 'fieldIcon.alt', '')}
                    imageTitle={get(component, 'fieldIcon.title', '')}
                    text={{
                      t1: get(component, 'fieldImageDescription[0].processed', ''),
                      t2: get(component, 'fieldImageDescription[1].processed', ''),
                      t3: get(component, 'fieldImageDescription[2].processed', ''),
                      t4: get(component, 'fieldImageDescription[3].processed', ''),
                    }}
                  />
                )}
              </Fragment>
            ))}
          </div>
        ))}
    </div>
  );
};

export default PageSections;
